/**
 * Theme provider service.
 *
 * Drives style, text copy, and user functionality for all components.
 * Minimise theme specific logic by using theme settings.
 *
 * Store specific themes integrated into the code in the short-term.
 * Long-term provided by server, configured by shopify app and/or theme settings
 */

import { getMode } from "@stencil/core"
import { FP, DateFns, Simplify } from "../deps"
import * as ServiceShopify from "../service-shopify/service-shopify"
import * as ServiceSellalong from "../service-sellalong/service-sellalong"

type SamplePurchase = Simplify<
  Omit<ServiceSellalong.Purchase, "isSample"> & {
    isSample: true
  }
>

type MonetaryAmount = {
  currency: string
  value: number
}

/** Supported languages */
enum Language {
  en = "en",
}

export type Theme = {
  settings: ThemeConfig["settings"]
  text: <K extends keyof ThemeConfig["text"][Language.en]>(
    key: K,
    variant?: string | null
  ) => ThemeConfig["text"][Language.en][K]
}

export type ThemeConfig = {
  settings: {
    /** Minimum amount for max credit display in the CTA */
    ctaMaxCreditDisplayThreshold: number

    ctaActionClasses: ReadonlyArray<string>

    discountDisplayElement: keyof HTMLElementTagNameMap

    /** Sample purchases displayed in Panel when no user detected (logged-out) */
    samplePurchases: ReadonlyArray<SamplePurchase>

    panelCheckoutElement: keyof HTMLElementTagNameMap
    panelCheckoutClasses: ReadonlyArray<string>
    panelContinueShoppingClasses: ReadonlyArray<string>
    panelItemSpaceBetween: number
    panelNavCentered: boolean

    purchaseItemCtaClasses: {
      OWNED: ReadonlyArray<string>
      TOSELL: ReadonlyArray<string>
    }

    purchaseItemShowSeller: boolean

    /** Minimum age in months for purchases eligible for sell-back */
    purchaseMinAge: number
  }

  text: Record<
    Language,
    {
      ctaLeading: string | null
      ctaMain: (input: {
        maxCredit: MonetaryAmount
        maxCreditDisplayThreshold: number
      }) => string
      ctaLink: string
      ctaCurrentCredit: (input: { credit: MonetaryAmount }) => string
      ctaCurrentCreditLink: string
      discountDisplayTitle: string
      formatCurrency: typeof formatCurrency
      helpModalTitle: string
      helpModalLeading: string
      helpModalStep1Title: string
      helpModalStep1Description: string
      helpModalStep2Title: string
      helpModalStep2Description: string
      helpModalStep3Title: string
      helpModalStep3Description: string
      helpModalStep3Note: string
      panelTitle: string
      panelStep1: string
      panelStep2: string
      panelStep3: string
      panelFooterStatus: (input: {
        selectedCount: number
        credit: MonetaryAmount
      }) => string
      panelFooterRolledOver: (input: {
        rolledOverCredit: MonetaryAmount | null
      }) => string
      panelFooterAdditionalCredit: (input: { credit: MonetaryAmount }) => string
      panelFooterCheckoutCTA: string
      panelFooterContinueShopping: string
      panelLoginCTA: string
      panelLoginSubtitle: string
      panelItemFormatCurrency: (
        input: FP.option.Option<MonetaryAmount>
      ) => string
      panelItemStatusMessage: (input: {
        state:
          | "OWNED"
          | "TOSELL"
          | "SELLING"
          | "SOLD"
          | "UNAVAILABLE"
          | "NEW"
          | "LOADING"
        minAge: number
      }) => string | null
      panelItemPurchaseAge: (orderDate: Date) => string
    }
  >
}

const samplePurchaseDate = (monthsOld: number): SamplePurchase["orderDate"] =>
  FP.function.pipe(
    DateFns.subMonths(monthsOld),
    FP.function.apply(Date.now()),
    DateFns.formatISO as <A>(a: A) => SamplePurchase["orderDate"]
  )

/** Sample purchases for signed out users */
const samplePurchases: ReadonlyArray<SamplePurchase> = [
  {
    id: "sample1",
    seller: "Cecilia Hammarborg",
    name: "Classic Hackney Coat, Black - Small / Black",
    image:
      "https://cdn.shopify.com/s/files/1/2721/9324/products/CeciliaHammarborgMarketing-10_200x150@2x.jpg?v=1612814434" as any,
    offer: {
      price: {
        currency: "GBP",
        value: 44.7,
      },
      pricingDescriptor: null,
    },
    state: ServiceSellalong.PurchaseState.Owned,
    isSample: true,
    orderDate: samplePurchaseDate(2),
  },
  {
    id: "sample2",
    seller: "GROUNDTRUTH",
    name: "15L Technical Tote",
    image:
      "https://cdn.shopify.com/s/files/1/2721/9324/products/BuyMeonce_Groundtruth_techtote_ecomm1_200x150@2x.jpg?v=1605868092" as any,
    offer: {
      price: {
        currency: "GBP",
        value: 37.6,
      },
      pricingDescriptor: null,
    },
    state: ServiceSellalong.PurchaseState.Owned,
    isSample: true,
    orderDate: samplePurchaseDate(4),
  },
  {
    id: "sample3",
    image:
      "https://cdn.shopify.com/s/files/1/2721/9324/products/mens-fitted-grey-marl-t-shirt11_200x150@2x.jpg?v=1619195563" as any,
    seller: "The White T-Shirt Company",
    name: "Fitted Round Neck T-Shirt - M / Black",
    offer: {
      price: {
        currency: "GBP",
        value: 8.5,
      },
      pricingDescriptor: null,
    },
    state: ServiceSellalong.PurchaseState.Owned,
    isSample: true,
    orderDate: samplePurchaseDate(14),
  },
  {
    id: "sample4",
    seller: "Darn Tough",
    name: "Women's Solid Basic Crew Light Socks - S / Charcoal",
    image:
      "https://cdn.shopify.com/s/files/1/2721/9324/products/BuyMeOnce_DarnTough_ecomm_200x150@2x.jpg" as any,
    offer: {
      price: {
        currency: "GBP",
        value: 2.35,
      },
      pricingDescriptor: null,
    },
    state: ServiceSellalong.PurchaseState.Owned,
    isSample: true,
    orderDate: samplePurchaseDate(18),
  },
]

const defaultThemeConfig: ThemeConfig = {
  settings: {
    ctaMaxCreditDisplayThreshold: 10,
    ctaActionClasses: [],
    discountDisplayElement: "div",
    samplePurchases,
    panelCheckoutClasses: [],
    panelCheckoutElement: "input",
    panelContinueShoppingClasses: [],
    panelItemSpaceBetween: 90,
    panelNavCentered: false,
    purchaseItemCtaClasses: {
      OWNED: [],
      TOSELL: [],
    },
    purchaseItemShowSeller: true,
    purchaseMinAge: 1,
  },
  text: {
    [Language.en]: {
      ctaLeading: null,
      ctaMain: ({ maxCredit, maxCreditDisplayThreshold }) =>
        `Get instant credit${
          maxCredit.value < maxCreditDisplayThreshold
            ? ""
            : " of up to " + formatCurrency(maxCredit)
        } for your\u00A0past\u00A0purchases`,
      ctaLink: "Show me how",
      ctaCurrentCredit: ({ credit }) =>
        `Including your sell-back credit of\u00A0${formatCurrency(credit)}`,
      ctaCurrentCreditLink: "Show details",
      discountDisplayTitle: "Subtotal",
      formatCurrency,

      helpModalTitle:
        "Sell us your past purchases for\u00A0instant\u00A0credit",
      helpModalLeading:
        "If you have any past purchases you’re no longer using, sell them back to us and we’ll pass them along to someone who\u00A0wants\u00A0them.",
      helpModalStep1Title: "Select items you're no longer\u00A0using",
      helpModalStep1Description:
        "Don't worry about the condition (everything has value)",
      helpModalStep2Title: "Receive instant credit",
      helpModalStep2Description:
        "Apply the credit towards a new purchase - instantly",
      helpModalStep3Title: "Send back for free within 14\u00A0days",
      helpModalStep3Description:
        "We'll email you a prepaid post label. Use the box from your new items, or any box or bag that works",
      helpModalStep3Note:
        "Note: You’ll be charged for items not shipped within 14\u00A0days",

      panelTitle: "Sell us your past purchases for instant credit",
      panelStep1: "Select items you’re no longer using",
      panelStep2: "Receive instant credit",
      panelStep3: "Send back for free within 14 days",
      panelFooterStatus: ({ selectedCount, credit }) =>
        selectedCount > 0
          ? `You’ve selected ${selectedCount} items to sell-back for ${formatCurrency(
              credit
            )} instant credit.`
          : "Select from your items above to sell-back for instant credit.",
      panelFooterRolledOver: ({ rolledOverCredit }) =>
        rolledOverCredit !== null
          ? `Including ${formatCurrency(
              rolledOverCredit
            )} of credit from your last sell-back.`
          : "We'll cover the postage and send you the details after checkout.",
      panelFooterAdditionalCredit: ({ credit }) =>
        `Your additional ${formatCurrency(
          credit
        )} in credit will automatically be applied on your next checkout.`,
      panelFooterCheckoutCTA: "Check out",
      panelFooterContinueShopping: "Add something new",
      panelLoginCTA: "Login or Register to Start",
      panelLoginSubtitle:
        "We’ll match your purchase history to your email address",

      panelItemFormatCurrency: FP.option.fold(
        FP.function.constant("-"),
        formatCurrency
      ),
      panelItemPurchaseAge: (orderDate) =>
        `${DateFns.formatDistance(orderDate)(Date.now())} old`,

      panelItemStatusMessage: ({ minAge, state }) =>
        state === "NEW"
          ? `Items are not eligible for resale within ${minAge} months of purchase`
          : state === "SELLING"
          ? "Item sold recently, awaiting delivery"
          : state === "SOLD"
          ? "Item has been sold"
          : state === "UNAVAILABLE"
          ? "Limited items are available for resale - please check\u00A0again\u00A0later"
          : null,
    },
  },
}

// Short term storage for theme definitions
const themes: Record<ServiceShopify.ShopId, ThemeConfig> = {
  [ServiceShopify.ShopId.SellalongDevelopment]: defaultThemeConfig,

  [ServiceShopify.ShopId.BuyMeOnce]: {
    settings: {
      ...defaultThemeConfig.settings,
      discountDisplayElement: "h2",
      panelCheckoutClasses: ["button", "button--large"],
      panelCheckoutElement: "input",
      panelNavCentered: true,
      purchaseMinAge: 3,
    },
    text: {
      [Language.en]: {
        ...defaultThemeConfig.text[Language.en],

        ctaLeading: "Any long-lasting items you’re no\u00A0longer\u00A0using?",
        helpModalLeading:
          "Help us keep long-lasting products in use forever. If you have any past purchases you’re no longer using, sell them back to us and we’ll pass them along to someone who\u00A0wants\u00A0them.",
      },
    },
  },

  [ServiceShopify.ShopId.Sirplus]: {
    settings: {
      ...defaultThemeConfig.settings,
      ctaActionClasses: ["Link", "Link--underline", "Link--underlineShort"],
      discountDisplayElement: "p",
      panelCheckoutClasses: [
        "Cart__Checkout",
        "Button",
        "Button--primary",
        "Button--full",
      ],
      panelCheckoutElement: "button",
      panelContinueShoppingClasses: [
        "Link",
        "Link--underline",
        "Link--underlineShort",
      ],
      panelItemSpaceBetween: 66.66,
      purchaseItemCtaClasses: {
        OWNED: ["Button", "Button--secondary"],
        TOSELL: ["Button", "Button--primary"],
      },
      purchaseItemShowSeller: false,
      purchaseMinAge: 6,

      samplePurchases: [
        // https://sirplus.co.uk/products/duck-egg-peak-lapel-double-breasted-waistcoat
        {
          id: "sirplusSample1",
          seller: "Sirplus",
          name: "Peak Lapel Double Breasted Waistcoat",
          image:
            "https://cdn.shopify.com/s/files/1/0862/9350/products/DUCKEGGPEAKLAPELDOUBLEBREASTEDWAISTCOAT_200x150_crop_top@2x.jpg" as SamplePurchase["image"],
          offer: {
            price: {
              currency: "GBP",
              value: 44.7,
            },
            pricingDescriptor: null,
          },
          state: ServiceSellalong.PurchaseState.Owned,
          isSample: true,
          orderDate: samplePurchaseDate(2),
        },
        // https://sirplus.co.uk/collections/shirts/products/navy-cotton-cashmere-grandad-shirt-1
        {
          id: "sirplusSample2",
          seller: "Sirplus",
          name: "Cotton Cashmere Grandad Shirt",
          image:
            "https://cdn.shopify.com/s/files/1/0862/9350/products/Navy_Cotton_Cashmere_Grandad_Shirt_bc37bfff-3110-466c-b3e7-73ec70e0e30b_200x150_crop_top@2x.jpg" as SamplePurchase["image"],
          offer: {
            price: {
              currency: "GBP",
              value: 37.6,
            },
            pricingDescriptor: null,
          },
          state: ServiceSellalong.PurchaseState.Owned,
          isSample: true,
          orderDate: samplePurchaseDate(4),
        },
        // https://sirplus.co.uk/products/deep-indigo-chore-jacket
        {
          id: "sirplusSample3",
          seller: "Sirplus",
          name: "Deep Indigo Chore Jacket",
          image:
            "https://cdn.shopify.com/s/files/1/0862/9350/products/edited3_DEEPINDIGOCHOREJACKET_200x150_crop_top@2x.jpg" as SamplePurchase["image"],
          offer: {
            price: {
              currency: "GBP",
              value: 29.45,
            },
            pricingDescriptor: null,
          },
          state: ServiceSellalong.PurchaseState.Owned,
          isSample: true,
          orderDate: samplePurchaseDate(12),
        },
        // https://sirplus.co.uk/products/new-blue-herringbone-socks
        {
          id: "sirplusSample4",
          seller: "Sirplus",
          name: "Blue Herringbone Socks",
          image:
            "https://cdn.shopify.com/s/files/1/0862/9350/products/BLUEHERRINGBONESOCKS_200x150_crop_center@2x.jpg" as SamplePurchase["image"],
          offer: {
            price: {
              currency: "GBP",
              value: 4.85,
            },
            pricingDescriptor: null,
          },
          state: ServiceSellalong.PurchaseState.Owned,
          isSample: true,
          orderDate: samplePurchaseDate(9),
        },
      ],
    },
    text: {
      [Language.en]: {
        ...defaultThemeConfig.text[Language.en],
        discountDisplayTitle: "Total",
        panelTitle: "Sell your past purchases for instant credit",
        panelFooterCheckoutCTA: "Checkout",
        panelFooterStatus: ({ selectedCount, credit }) =>
          selectedCount > 0
            ? `Selected ${selectedCount} items to sell-back for ${formatCurrency(
                credit
              )} instant credit.`
            : "Select your items to sell-back for instant credit.",
        panelFooterRolledOver: ({ rolledOverCredit }) =>
          rolledOverCredit !== null
            ? `Including ${formatCurrency(
                rolledOverCredit
              )} of credit from your last sell-back.`
            : "Free postage will be emailed to you after checkout.",
      },
    },
  },
}

export const loadTheme: (elem: Element) => FP.task.Task<Theme> =
  (elem) => async () =>
    FP.function.pipe(
      elem,
      getMode,
      FP.option.fromNullable,
      FP.option.chainNullableK(parseShopIdFromMode),
      FP.option.chain(ServiceShopify.toShopId),
      FP.option.getOrElse(
        FP.function.constant(ServiceShopify.ShopId.SellalongDevelopment)
      ),
      (shopId) => getTheme(shopId, getLanguage(elem))
    )

function parseShopIdFromMode(mode: string) {
  const parsed = /^theme_(\w+)$/.exec(mode)
  return parsed === null ? null : parsed[1]
}

function getTheme(shopId: ServiceShopify.ShopId, language: Language): Theme {
  const theme = themes[shopId]

  return {
    settings: theme.settings,
    text(key) {
      return theme.text[language][key]
    },
  }
}

function getLanguage(elem: Element): Language {
  const lang = elem.ownerDocument.documentElement.lang
  return (lang && (Language as Record<string, Language>)[lang]) || Language.en
}

// TODO use currency.js instead
function formatCurrency({ currency, value }: MonetaryAmount) {
  return `${currency === "GBP" ? "£" : ""}${value.toFixed(2)}`
}
